/**
 * Cache Service for AI Chatbot
 * Implements in-memory caching with TTL for business data and AI responses
 */

interface CacheItem<T> {
  data: T;
  expiry: number;
  createdAt: number;
}

export class CacheService {
  private cache = new Map<string, CacheItem<any>>();
  private maxSize = 1000; // Maximum number of items in cache
  private cleanupInterval: NodeJS.Timeout;

  constructor() {
    // Clean up expired items every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 5 * 60 * 1000);
  }

  /**
   * Get item from cache
   */
  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    if (!item || Date.now() > item.expiry) {
      if (item) {
        this.cache.delete(key); // Remove expired item
      }
      return null;
    }
    return item.data as T;
  }

  /**
   * Set item in cache with TTL
   */
  set<T>(key: string, data: T, ttlMs: number): void {
    // Remove oldest items if cache is full
    if (this.cache.size >= this.maxSize) {
      this.evictOldest();
    }

    this.cache.set(key, {
      data,
      expiry: Date.now() + ttlMs,
      createdAt: Date.now()
    });
  }

  /**
   * Check if key exists and is not expired
   */
  has(key: string): boolean {
    const item = this.cache.get(key);
    return item !== undefined && Date.now() <= item.expiry;
  }

  /**
   * Remove item from cache
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Clear all items from cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const now = Date.now();
    let expired = 0;
    let valid = 0;

    for (const [, item] of Array.from(this.cache.entries())) {
      if (now > item.expiry) {
        expired++;
      } else {
        valid++;
      }
    }

    return {
      total: this.cache.size,
      valid,
      expired,
      hitRate: this.calculateHitRate()
    };
  }

  /**
   * Clean up expired items
   */
  private cleanup(): void {
    const now = Date.now();
    for (const [key, item] of Array.from(this.cache.entries())) {
      if (now > item.expiry) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Remove oldest item when cache is full
   */
  private evictOldest(): void {
    let oldestKey = '';
    let oldestTime = Date.now();

    for (const [key, item] of Array.from(this.cache.entries())) {
      if (item.createdAt < oldestTime) {
        oldestTime = item.createdAt;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Calculate approximate hit rate (simplified)
   */
  private calculateHitRate(): number {
    // This is a simplified calculation
    // In a real implementation, you'd track hits/misses
    return this.cache.size > 0 ? 0.8 : 0; // Placeholder
  }

  /**
   * Destroy cache and cleanup
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.cache.clear();
  }
}

// Cache keys for different data types
export const CACHE_KEYS = {
  BUSINESS_DATA: (tenantId: string) => `business_data:${tenantId}`,
  AI_RESPONSE: (tenantId: string, query: string) => `ai_response:${tenantId}:${hashQuery(query)}`,
  POPULAR_ITEMS: (tenantId: string) => `popular_items:${tenantId}`,
  TOP_CUSTOMERS: (tenantId: string) => `top_customers:${tenantId}`,
  SALES_METRICS: (tenantId: string) => `sales_metrics:${tenantId}`,
  INVENTORY_STATUS: (tenantId: string) => `inventory_status:${tenantId}`
};

// Cache TTL constants (in milliseconds)
export const CACHE_TTL = {
  BUSINESS_DATA: 5 * 60 * 1000,      // 5 minutes
  AI_RESPONSE: 30 * 1000,            // 30 seconds
  POPULAR_ITEMS: 10 * 60 * 1000,     // 10 minutes
  TOP_CUSTOMERS: 15 * 60 * 1000,     // 15 minutes
  SALES_METRICS: 2 * 60 * 1000,      // 2 minutes
  INVENTORY_STATUS: 5 * 60 * 1000    // 5 minutes
};

/**
 * Hash query for cache key generation
 */
function hashQuery(query: string): string {
  // Simple hash function for query strings
  let hash = 0;
  for (let i = 0; i < query.length; i++) {
    const char = query.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

// Singleton instance
export const cacheService = new CacheService();
